import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auth, intelligence, programs } from '@cwos/api-client';
import { t } from '@cwos/ui-shared';
import { Icon, SkeletonSet } from '@cwos/ui-admin';
import { ConversationSurface } from './global/ConversationSurface.js';
import { HistoryPane } from './history/HistoryPane.js';
import { useThreadsClient } from './history/threadsClient.js';
import type { IntelligenceThread } from './history/threads.js';
import { deriveSuggestions, firstNameOf, greetingKeyFor, type Suggestion } from './suggestions.js';

/**
 * Ask Phibian — the assistant workspace.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN STOPPED BEING.
 *
 * It was an engineering interface with a chat box in it. A person who asked
 * how many hours a program delivered was shown, in order: a capability
 * manifest listing `dashboard.plan` and `analytics.query_metric@1` with their
 * model-facing descriptions; a step-by-step replay of the tool loop ("Listed
 * what can be measured", "Worked out the numbers", "Drafted a dashboard for
 * you to confirm"); an evidence disclosure counted in "retrievals"; a nested
 * "Developer detail" printing manifest ordinals and content hashes; and a plan
 * digest rendered as a truncated hash. All of it was true. None of it was for
 * them.
 *
 * Four things were removed outright rather than restyled — the capability
 * manifest, the completed-answer activity trail, the developer disclosures and
 * the printed plan digest. The reasoning for each sits next to what replaced
 * it: `ConversationSurface.tsx` for the trail, the disclosures and the digest,
 * `evidenceView.ts` for the provenance lines, and the empty state below for
 * the manifest.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * F224 IS CLOSED. The page used to carry a private copy of the conversation —
 * its own thread, dock, composer, evidence panel and confirmation card — while
 * `global/ConversationSurface.tsx` carried a second, prop-driven copy for the
 * floating panel. The page now renders that component and supplies only the
 * chrome around it: the header, the history sidebar and the empty state. One
 * conversation implementation, two hosts.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE LAYOUT: history | conversation, inside the shell's existing nav rail.
 *
 * The rail is untouched — this is a pane WITHIN the Intelligence screen, not a
 * second navigation. On a desktop the two sit side by side and the history
 * pane collapses; below the breakpoint it becomes a sheet over the
 * conversation, because the conversation is the screen and must never be
 * squeezed into a narrow centre column to make room for a list.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * HISTORY IS SERVER STATE AND IS NOT BUILT HERE. This screen codes against the
 * port in `history/threads.ts`, which is bound in exactly one place
 * (`history/threadsClient.ts`) over the six durable thread routes the API
 * mounts. There is deliberately no local-storage stand-in — see the note in
 * `threadsClient.ts` for why a convincing fake is worse than an honest absence.
 *
 * WHAT THIS SCREEN OWNS OF THAT, and it is only two things. A NEW conversation
 * has no thread until its first message, because the server derives the title
 * from that message and this product has no placeholder title — so the create
 * is a hook the conversation surface calls at the moment it has one
 * (`ensureThread`). And a REOPENED conversation is replayed from the server's
 * stored transcript rather than resumed silently, because a reopened thread
 * that renders empty is indistinguishable from a fresh one.
 */

/** What the screen is, for the route's denied state (RequirePermission `what`). */
export const INTELLIGENCE_IS =
  'Intelligence is the assistant surface: ask questions about your organization in plain words and get answers grounded in the records your account can already read. Every answer names the evidence it consulted, and any change the assistant proposes waits for your explicit confirmation before it runs. It reads with your permissions — it can never show you more than you could open yourself.';

export interface IntelligenceProps {
  /**
   * The clock behind the greeting and the history grouping. Injected the same
   * way `HeroGreeting` takes it — a screen that says "Good evening" and files
   * a thread under "Today" needs a time a test can pin, and a default argument
   * is the only honest way to have both.
   */
  now?: Date;
}

export function Intelligence({ now }: IntelligenceProps = {}) {
  const threadsClient = useThreadsClient();
  const queryClient = useQueryClient();

  /**
   * Which conversation is open, and the remount key that switches it.
   *
   * `ConversationSurface` owns a thread's exchanges and its session id, so
   * switching conversations means giving it a new identity rather than asking
   * it to reset itself — the `key` below does that, and a reset method that
   * had to remember to clear four pieces of state would be the same thing with
   * more ways to be wrong.
   */
  const [openThread, setOpenThread] = useState<IntelligenceThread | undefined>(undefined);
  const [conversationSeq, setConversationSeq] = useState(0);
  /** The history sheet, below the breakpoint. Desktop ignores it. */
  const [historyOpen, setHistoryOpen] = useState(false);

  const newChat = () => {
    setOpenThread(undefined);
    setConversationSeq((seq) => seq + 1);
    setHistoryOpen(false);
  };

  /**
   * The reopened conversation's stored transcript.
   *
   * `enabled` on BOTH the client and an open thread, so a new chat issues no
   * request. A failure here is not a failure of the conversation — the person
   * can carry on asking, and the server still holds the thread — so it is
   * handed to the empty state as its own sentence rather than replacing the
   * screen with an error.
   */
  const transcript = useQuery({
    queryKey: ['intelligence', 'threads', openThread?.threadId, 'messages'],
    queryFn: () => threadsClient!.messages({ threadId: openThread!.threadId }),
    enabled: threadsClient !== undefined && openThread !== undefined,
  });

  return (
    <div className="ap">
      <button
        type="button"
        className="ap-history-toggle"
        aria-expanded={historyOpen}
        onClick={() => setHistoryOpen((open) => !open)}
      >
        <Icon name="menu" size={16} />
        Conversations
      </button>

      {/* The scrim exists only in the sheet layout; on a desktop the pane is a
          column and this never renders. */}
      {historyOpen ? (
        <div
          className="ap-history-scrim"
          role="presentation"
          onClick={() => setHistoryOpen(false)}
        />
      ) : null}

      <div className={historyOpen ? 'ap-history-slot ap-history-slot-open' : 'ap-history-slot'}>
        <HistoryPane
          client={threadsClient}
          activeThreadId={openThread?.threadId}
          onNewChat={newChat}
          onSelectThread={(thread) => {
            setOpenThread(thread);
            setConversationSeq((seq) => seq + 1);
            setHistoryOpen(false);
          }}
          now={now}
        />
      </div>

      <section className="ap-main" aria-label="Ask Phibian">
        <header className="ap-head">
          <h1 className="ap-title">Ask Phibian</h1>
          <p className="ap-lede">
            Ask about your organization in plain words. Every answer is grounded in records your
            account can already read.
          </p>
        </header>

        <ConversationSurface
          key={conversationSeq}
          threadId={openThread?.threadId}
          transcript={transcript.data?.messages}
          ensureThread={
            threadsClient === undefined
              ? undefined
              : async (firstMessage) => {
                  const thread = await threadsClient.create({ firstMessage });
                  // The sidebar's list is now one row out of date, and the row
                  // it is missing is the conversation the person is in. Re-read
                  // rather than push a locally-built row: the title is the
                  // server's, derived from the message, and a client-built one
                  // would differ from what every other browser lists.
                  void queryClient.invalidateQueries({ queryKey: ['intelligence', 'threads'] });
                  return thread.threadId;
                }
          }
          renderEmpty={(ask, disabled) => (
            <EmptyState
              onPick={ask}
              now={now}
              disabled={disabled}
              resumed={openThread !== undefined}
              transcriptFailed={transcript.isError}
              transcriptPending={openThread !== undefined && transcript.isPending}
            />
          )}
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The caller's own authority, read once and shared.
// ---------------------------------------------------------------------------

/**
 * `/auth/me` on the SAME query key `usePermissions()` uses, so the two share
 * one cache entry and one request. This screen needs two different things off
 * that payload — the person's name for the greeting and the scope-aware `can`
 * for choosing between phrasings — and issuing a second `/auth/me` to get them
 * would be a second answer that can disagree with the first.
 */
function useMe() {
  return useQuery({
    queryKey: ['auth', 'me', 'permissions'],
    queryFn: () => auth.me(),
    staleTime: 5 * 60_000,
  });
}

/**
 * `GET /api/intelligence/tools` — what the server says THIS caller may use.
 *
 * The empty state is now the ONLY reader of this list, and it reads it to
 * decide what to SUGGEST, never to display. That is the whole difference
 * between the derivation in `suggestions.ts` and the capability manifest that
 * used to sit in the header: one turns authority into questions a person can
 * ask, the other turned it into a list of function names.
 */
function useAdvertisedTools() {
  return useQuery({
    queryKey: ['intelligence', 'tools'],
    queryFn: () => intelligence.tools(),
  });
}

// ---------------------------------------------------------------------------
// The empty state.
// ---------------------------------------------------------------------------

/**
 * Before the first message, the screen's whole job is to be useful without
 * having been asked anything.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE CAPABILITY MANIFEST IS GONE.
 *
 * A disclosure called "What Phibian can do for you" used to list the
 * advertised tools, with a nested developer view printing each id and the
 * description the MODEL reads when choosing a function. Two things were wrong
 * with it beyond the ids. It was a menu to study before a person was allowed
 * to type, on a surface whose whole promise is that they can just ask. And a
 * tool list is not a capability list: `dashboard.plan` and `dashboard.create`
 * are two entries for one thing a person wants, and `analytics.query_metric`
 * is every numerical question there is.
 *
 * What replaces it is what the manifest was standing in for: a short line of
 * plain-language capabilities, and starters DERIVED FROM REAL AUTHORITY. The
 * capability lines are fixed product copy — they describe the product, not
 * this caller's grants, so they never imply access. Everything that does imply
 * access is derived, through the one derivation in `suggestions.ts`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE THREE STATES OF THE SUGGESTION STRIP, and why there are three: the
 * authority is NOT KNOWN YET (a skeleton — silence, not a default list), it is
 * known and supports some suggestions (those, and only those), or it is known
 * and supports none (a plain sentence saying so). Collapsing the first into
 * the third would tell a person their account can ask nothing, on the strength
 * of a request that had not come back.
 */
function EmptyState({
  onPick,
  now,
  disabled,
  resumed,
  transcriptFailed,
  transcriptPending,
}: {
  onPick: (question: string) => void;
  now?: Date;
  disabled: boolean;
  /** True when the person reopened a conversation from history. */
  resumed: boolean;
  /** True when the stored transcript could not be read. */
  transcriptFailed: boolean;
  /** True while the stored transcript is still being read. */
  transcriptPending: boolean;
}) {
  // A REOPENED CONVERSATION WHOSE TRANSCRIPT HAS NOT ARRIVED IS NOT A NEW ONE.
  // The greeting and the starter questions are what a FRESH conversation
  // shows, and showing them over a thread that is about to fill with what was
  // said would tell the person, for as long as the read takes, that their
  // conversation is empty.
  if (transcriptPending) {
    return (
      <section className="ap-empty" aria-label="Getting started">
        <p className="rn-muted ap-resumed" role="status">
          Loading what was said in this conversation…
        </p>
      </section>
    );
  }
  const meQuery = useMe();
  const toolsQuery = useAdvertisedTools();

  // Programs are read only when the caller holds `program:read` somewhere —
  // `GET /programs` NARROWS to the grant rather than refusing, so "holds it at
  // any scope" is the right question here, and an account without it never pays
  // for the request. Same query key the other program pickers use.
  const mayReadPrograms = auth.holdsAtAnyScope(meQuery.data, 'program:read');
  const programsQuery = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ status: 'active', limit: 200 }),
    enabled: mayReadPrograms,
  });

  // Authority is "known" only when BOTH reads have landed. `can` default-denies
  // while `/auth/me` is in flight, and a default-denied `can` would hand a
  // supervisor the self-scoped phrasing of a question they hold the whole
  // organization for.
  const authorityKnown = toolsQuery.isSuccess && meQuery.isSuccess;
  const advertisedToolIds = authorityKnown
    ? toolsQuery.data.tools.map((tool) => tool.name)
    : undefined;

  // Sorted for determinism: the wire order of `/programs` is the server's
  // paging order, and a suggestion that names two programs must name the same
  // two on every render.
  const programNames = programsQuery.isSuccess
    ? programsQuery.data.programs.map((program) => program.name).sort((a, b) => a.localeCompare(b))
    : undefined;

  const suggestions: Suggestion[] = deriveSuggestions({
    advertisedToolIds,
    can: (key) => auth.can(meQuery.data, key),
    programNames,
  });

  const first = firstNameOf(meQuery.data?.name);
  const greeting = t(greetingKeyFor((now ?? new Date()).getHours()));

  return (
    <section className="ap-empty" aria-label="Getting started">
      {/* Two elements, not one heading with an inline span: an accessible name
          is assembled by concatenating TRIMMED descendant text, so the span
          form announced as "Abdirahman.What do you want to know?". The line
          break is also the better read — the greeting names the person, the
          question hands them the turn. */}
      <div className="ap-greeting">
        <h2 className="ap-greet">
          {first === undefined ? `${greeting}.` : `${greeting}, ${first}.`}
        </h2>
        <p className="ap-greet-ask">What would you like to know?</p>
      </div>

      {resumed && transcriptFailed ? (
        // A reopened conversation whose transcript would not load. The thread
        // exists and asking still works, so this is not a broken screen — but
        // an empty one is indistinguishable from a fresh conversation, and
        // saying nothing would let a person believe they had lost it.
        <p className="rn-muted ap-resumed" role="status">
          We couldn’t load what was said in this conversation earlier. It isn’t gone — we just
          couldn’t read it back right now. Anything you ask below is added to it as normal.
        </p>
      ) : null}

      <ul className="ap-canlist" aria-label="What Phibian is for">
        {CAPABILITY_LINES.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      {!authorityKnown && (toolsQuery.isLoading || meQuery.isLoading) ? (
        <div className="ap-suggest-skeleton">
          <SkeletonSet surface="tiles" rows={2} />
        </div>
      ) : suggestions.length > 0 ? (
        <ul className="ap-suggestions" aria-label="Suggested questions">
          {suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <button
                type="button"
                className="ap-suggestion"
                disabled={disabled}
                onClick={() => onPick(suggestion.text)}
              >
                <span className="ap-suggestion-text">{suggestion.text}</span>
                <Icon name="arrowRight" size={14} className="ap-suggestion-go" />
              </button>
            </li>
          ))}
        </ul>
      ) : authorityKnown ? (
        <p className="rn-muted ap-nosuggest">
          There are no starter questions to offer your account right now — this screen only suggests
          things your permissions can actually answer. Ask in your own words below.
        </p>
      ) : (
        <p className="rn-muted ap-nosuggest">
          We couldn’t check what your account can ask about, so there are no starters here. That is
          a failed check, not a decision about your access — ask in your own words below.
        </p>
      )}
    </section>
  );
}

/**
 * What the product is for, in five plain lines.
 *
 * FIXED COPY, and that is the point. These describe Phibian, not this
 * caller's grants, so they cannot overstate anyone's access — "Build
 * dashboards" is what the product does; whether THIS account may build one is
 * answered by the derived starters below and, finally, by the server. The old
 * manifest tried to make one list do both jobs and ended up naming tools to do
 * either.
 */
const CAPABILITY_LINES = [
  'Ask questions about your organization',
  'Build dashboards',
  'Explain trends',
  'Find operational issues',
  'Create automations',
] as const;
